import React from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router'
import { Dropdown, DropdownButton } from 'react-bootstrap'
import Select from 'react-select'

import FilterSearch from 'pages/parts/FilterSearch'
import PosSearchSelect from 'pages/parts/tasks/PosSearchSelect'
import { formatDate } from 'helpers/helpers'

function TasksBoardToolbar({
	apiPrefix,
	filterData,
	patchFilterData,
	partners = [],
	categories = [],
	dropdownIsOpen = false,
	setDropdownIsOpen = null,
	kpis = [],
	deadlineCounts = [],
	activeChip = 'all',
	boardTotal = 0,
	categoriesPath = null,
	onSearchChange = null,
	onSelectEmployee = null,
	onApplyFilters = null,
	onResetFilters = null,
	onSelectChip = null,
	onCreate = null,
	onImport = null,
	onExport = null,
	onDownloadTemplate = null,
}) {
	const { t } = useTranslation()

	function returnChipLabel(item) {
		if (item.key === 'today') return t('today')
		if (item.key === 'tomorrow') return t('tomorrow')
		if (item.key === 'month') return item.month || t('month')
		return item.date ? formatDate(item.date, 'dd.MM') : t('date')
	}

	function returnChipValue(item) {
		if (item.key === 'month') return 'month:' + item.month
		return 'date:' + item.date
	}

	return (
		<>
			<div className="card mb-3">
				<div className="card-body">
					<div className="tasks-toolbar">
						<div className="tasks-toolbar-select">
							<Select
								isClearable
								options={partners}
								value={partners.find(option => option.id === filterData.user_id) || ''}
								onChange={(option) => onSelectEmployee(option)}
								placeholder={t('task_all_employees')}
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name || option.login}
								getOptionValue={(option) => option.id}
							/>
						</div>

						<div className="tasks-search">
							<FilterSearch
								value={filterData.search}
								onSearchChange={(search) => onSearchChange(search)}>
							</FilterSearch>
						</div>

						<Dropdown onToggle={(bool) => setDropdownIsOpen(bool)} show={dropdownIsOpen}>
							<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
								{t('filter')}
							</Dropdown.Toggle>
							<Dropdown.Menu className="dropdown-filter">
								<div className="row">
									<div className="form-group col-md-6">
										<label>{t('category')}</label>
										<Select
											isClearable
											options={categories}
											value={categories.find(option => option.id === filterData.category_id) || ''}
											onChange={(option) => patchFilterData({ 'category_id': option ? option.id : null })}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									</div>
									<div className="form-group col-md-6">
										<label>{t('pos')}</label>
										<PosSearchSelect
											apiPrefix={apiPrefix}
											value={filterData.pos}
											onChange={(option) => patchFilterData({ 'pos': option || null })}>
										</PosSearchSelect>
									</div>
								</div>
								<div className="row mt-2">
									<div className="col-md-12">
										<div className="form-check form-switch form-switch-md">
											<input type="checkbox" className="form-check-input" id="tasks-only-overdue"
												checked={!!filterData.is_overdue}
												onChange={(e) => patchFilterData({ 'is_overdue': e.target.checked ? 1 : null })} />
											<label className="form-check-label" htmlFor="tasks-only-overdue">
												{t('task_only_overdue')}
											</label>
										</div>
									</div>
								</div>

								{/* Задачи крона живут своей жизнью, поэтому их удобно смотреть отдельно от ручных */}
								<div className="row mt-2">
									<div className="col-md-12">
										<div className="form-check form-switch form-switch-md">
											<input type="checkbox" className="form-check-input" id="tasks-only-system"
												checked={filterData.source === 'system'}
												onChange={(e) => patchFilterData({ 'source': e.target.checked ? 'system' : null })} />
											<label className="form-check-label" htmlFor="tasks-only-system">
												{t('task_only_system')}
											</label>
										</div>
									</div>
								</div>
								<div className="row mt-3">
									<div className="col-md-12">
										<div className="d-flex justify-content-end gap-2">
											<button className="btn btn-secondary" onClick={() => onResetFilters()}>
												{t('reset')}
											</button>
											<button className="btn btn-primary" onClick={() => onApplyFilters()}>
												{t('apply')}
											</button>
										</div>
									</div>
								</div>
							</Dropdown.Menu>
						</Dropdown>

						<div className="d-flex gap-2 ms-auto">
							{categoriesPath &&
								<NavLink to={categoriesPath} className="btn btn-outline-primary btn-wide-2"
									title={t('task_categories')}>
									<i className="uil uil-list-ul"></i>
								</NavLink>
							}

							<DropdownButton title={t('action')} variant="outline-primary">
								<Dropdown.Item as="li" eventKey="1">
									<input id="tasks-import-btn" hidden type="file" onChange={onImport}
										accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" />
									<label htmlFor="tasks-import-btn" className="upload-from-excel">{t('import')}</label>
								</Dropdown.Item>
								<Dropdown.Item as="li" eventKey="2" onClick={() => onExport()}>
									<span className="upload-from-excel">{t('task_export')}</span>
								</Dropdown.Item>
								<Dropdown.Item as="li" eventKey="3" onClick={() => onDownloadTemplate()}>
									<span className="upload-from-excel">{t('download_template')}</span>
								</Dropdown.Item>
							</DropdownButton>

							<button className="btn btn-success" onClick={() => onCreate()}>
								<i className="uil uil-plus me-1"></i>
								{t('task_create')}
							</button>
						</div>
					</div>
				</div>
			</div>

			<div className="tasks-chips">
				<div className={'tasks-chip' + (activeChip === 'all' ? ' active' : '')}
					onClick={() => onSelectChip('all', null)}>
					{t('all')}
					{activeChip === 'all' &&
						<span className="tasks-chip-count">{boardTotal}</span>
					}
				</div>

				{deadlineCounts?.map((item, index) => (
					<div key={index}
						className={'tasks-chip' + (activeChip === returnChipValue(item) ? ' active' : '')}
						onClick={() => onSelectChip(returnChipValue(item), item)}>
						{returnChipLabel(item)}
						<span className="tasks-chip-count">{item.total || 0}</span>
					</div>
				))}
			</div>

			{kpis.length > 0 &&
				<div className="tasks-kpi-grid">
					{kpis.map((item, index) => (
						<div key={index} className="tasks-kpi-tile" style={{ borderLeftColor: item.color }}>
							<div className="tasks-kpi-label">{item.label}</div>
							<div className="tasks-kpi-value" style={{ color: item.color }}>{item.value}</div>
							{item.hint && <div className="tasks-kpi-hint">{item.hint}</div>}
						</div>
					))}
				</div>
			}
		</>
	)
}

export default TasksBoardToolbar
